import { motion } from "framer-motion"
import { RiDashboardLine } from "react-icons/ri"
import { FiSun, FiMoon, FiActivity, FiMenu, FiX, FiLogOut, FiShield, FiUser } from "react-icons/fi"
import { useState } from "react"
import { useAuth } from "../contexts/AuthContext"
import { useNavigate } from "react-router-dom"

const HeaderComponent = ({
    darkMode,
    toggleDarkMode,
    viewMode,
    setViewMode,
    secondaryTextColor,
}) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const { user, tenant, logout, isSystemAdmin } = useAuth()
    const navigate = useNavigate()

    const handleLogout = async () => {
        await logout()
        navigate("/login")
    }

    return (
        <>
            <motion.header
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6 md:mb-8"
            >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    {/* Main header content */}
                    <div className="flex justify-between items-center w-full md:w-auto">
                        <div className="flex items-center gap-3">
                            <RiDashboardLine 
                                className={`text-3xl md:text-4xl ${darkMode ? "text-blue-400" : "text-blue-600"}`} 
                            />
                            <div>
                                <h1 className={`text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r ${darkMode ? "from-blue-400 to-purple-500" : "from-blue-600 to-purple-600"} bg-clip-text text-transparent leading-tight`}>
                                    NexusFlow
                                </h1>
                                <p className={`${secondaryTextColor} mt-0.5 text-xs md:text-sm`}>
                                    {tenant?.name || "Real-time analytics"}
                                </p>
                            </div>
                        </div>
                        
                        {/* Mobile menu button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className={`md:hidden p-2 rounded-lg ${darkMode ? "hover:bg-gray-800" : "hover:bg-gray-200"} transition-colors`}
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? (
                                <FiX className={`text-xl ${darkMode ? "text-gray-300" : "text-gray-700"}`} />
                            ) : (
                                <FiMenu className={`text-xl ${darkMode ? "text-gray-300" : "text-gray-700"}`} />
                            )}
                        </button>
                    </div>

                    {/* Desktop controls */}
                    <div className="hidden md:flex items-center gap-4">
                        {/* View Mode Toggle */}
                        <div className={`flex rounded-lg overflow-hidden ${darkMode ? "bg-gray-800" : "bg-gray-200"}`}>
                            <button
                                onClick={() => setViewMode("cards")}
                                className={`px-4 py-2 text-sm font-medium transition-all ${viewMode === "cards" 
                                    ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white") 
                                    : (darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-300")}`}
                            >
                                Cards
                            </button>
                            <button
                                onClick={() => setViewMode("table")}
                                className={`px-4 py-2 text-sm font-medium transition-all ${viewMode === "table" 
                                    ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white") 
                                    : (darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-300")}`}
                            >
                                Table
                            </button>
                        </div>
                        
                        {/* Theme Switcher */}
                        <motion.button
                            onClick={toggleDarkMode}
                            whileTap={{ scale: 0.95 }}
                            className={`relative w-14 h-7 md:w-16 md:h-8 rounded-full ${darkMode ? "bg-blue-600" : "bg-gray-300"} transition-colors duration-300`}
                            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                        >
                            <motion.div
                                className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center ${darkMode ? "bg-gray-900" : "bg-white"} shadow-lg`}
                                animate={{ x: darkMode ? 28 : 0 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            >
                                {darkMode ? (
                                    <FiMoon className="text-blue-400 text-xs md:text-sm" />
                                ) : (
                                    <FiSun className="text-yellow-500 text-xs md:text-sm" />
                                )}
                            </motion.div>
                        </motion.button>
                        
                        {/* Status Indicator */}
                        <div className={`hidden lg:flex items-center gap-2 ${darkMode ? "bg-gray-800" : "bg-gray-100"} px-4 py-2 rounded-lg`}>
                            <FiActivity className={darkMode ? "text-green-400 animate-pulse" : "text-green-600 animate-pulse"} />
                            <span className="text-sm font-medium">Rules Engine Active</span>
                        </div>

                        {/* Admin Button (only for system admins) */}
                        {isSystemAdmin && (
                            <button
                                onClick={() => navigate("/admin")}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                                    darkMode 
                                        ? "bg-red-600 hover:bg-red-700" 
                                        : "bg-red-500 hover:bg-red-600"
                                } text-white transition-colors`}
                            >
                                <FiShield className="text-sm" />
                                <span className="text-sm font-medium">Admin</span>
                            </button>
                        )}

                        {/* User Info & Logout */}
                        <div className={`flex items-center gap-3 ${darkMode ? "bg-gray-800" : "bg-gray-100"} pl-4 pr-2 py-2 rounded-lg`}>
                            <div className="flex items-center gap-2">
                                <FiUser className={darkMode ? "text-gray-400" : "text-gray-600"} />
                                <div className="hidden xl:block">
                                    <p className="text-sm font-medium leading-tight">{user?.displayName || "User"}</p>
                                    <p className={`text-xs ${secondaryTextColor} leading-tight`}>{user?.role || "member"}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                                    darkMode 
                                        ? "bg-gray-700 hover:bg-gray-600" 
                                        : "bg-gray-200 hover:bg-gray-300"
                                } transition-colors`}
                                title="Logout"
                            >
                                <FiLogOut className="text-sm" />
                                <span className="text-sm font-medium hidden lg:inline">Logout</span>
                            </button>
                        </div>
                    </div>

                    {/* Mobile menu dropdown */}
                    <motion.div
                        initial={false}
                        animate={{ 
                            height: isMobileMenuOpen ? "auto" : 0,
                            opacity: isMobileMenuOpen ? 1 : 0
                        }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="w-full md:hidden overflow-hidden"
                    >
                        <div className={`${darkMode ? "bg-gray-800" : "bg-gray-100"} rounded-xl p-4 mt-2 shadow-lg`}>
                            <div className="space-y-4">
                                
                                {/* Mobile User Info */}
                                <div className={`flex items-center gap-3 p-3 rounded-lg ${darkMode ? "bg-gray-900" : "bg-gray-200"}`}>
                                    <div className={`p-2 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-300"}`}>
                                        <FiUser className={darkMode ? "text-gray-300" : "text-gray-600"} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">{user?.displayName || "User"}</p>
                                        <p className={`text-xs ${secondaryTextColor}`}>
                                            {tenant?.name || "No company"} • {user?.role || "member"}
                                        </p>
                                    </div>
                                </div>

                                {/* Mobile View Mode Toggle */}
                                <div>
                                    <p className={`text-sm font-medium mb-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                        View Mode
                                    </p>
                                    <div className={`grid grid-cols-2 gap-2 rounded-lg overflow-hidden ${darkMode ? "bg-gray-900" : "bg-gray-200"}`}>
                                        <button
                                            onClick={() => {
                                                setViewMode("cards")
                                                setIsMobileMenuOpen(false)
                                            }}
                                            className={`py-3 text-sm font-medium transition-all ${viewMode === "cards" 
                                                ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white") 
                                                : (darkMode ? "text-gray-300" : "text-gray-700")}`}
                                        >
                                            Cards
                                        </button>
                                        <button
                                            onClick={() => {
                                                setViewMode("table")
                                                setIsMobileMenuOpen(false)
                                            }}
                                            className={`py-3 text-sm font-medium transition-all ${viewMode === "table" 
                                                ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white") 
                                                : (darkMode ? "text-gray-300" : "text-gray-700")}`}
                                        >
                                            Table
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile Theme Switcher */}
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                        Theme
                                    </span>
                                    <motion.button
                                        onClick={toggleDarkMode}
                                        whileTap={{ scale: 0.95 }}
                                        className={`relative w-14 h-7 rounded-full ${darkMode ? "bg-blue-600" : "bg-gray-300"} transition-colors duration-300`}
                                        aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                                    >
                                        <motion.div
                                            className={`w-5 h-5 rounded-full flex items-center justify-center ${darkMode ? "bg-gray-900" : "bg-white"} shadow-lg`}
                                            animate={{ x: darkMode ? 28 : 0 }}
                                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                        >
                                            {darkMode ? (
                                                <FiMoon className="text-blue-400 text-xs" />
                                            ) : (
                                                <FiSun className="text-yellow-500 text-xs" />
                                            )}
                                        </motion.div>
                                    </motion.button>
                                </div>

                                {/* Mobile Status Indicator */}
                                <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? "bg-gray-900" : "bg-gray-200"}`}>
                                    <div className="flex items-center gap-2">
                                        <FiActivity className={darkMode ? "text-green-400 animate-pulse" : "text-green-600 animate-pulse"} />
                                        <span className="text-sm font-medium">Rules Engine Active</span>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded ${darkMode ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-700"}`}>
                                        Live
                                    </span>
                                </div>

                                {/* Mobile Admin Button (only for system admins) */}
                                {isSystemAdmin && (
                                    <button
                                        onClick={() => {
                                            navigate("/admin")
                                            setIsMobileMenuOpen(false)
                                        }}
                                        className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg ${
                                            darkMode 
                                                ? "bg-red-600 hover:bg-red-700" 
                                                : "bg-red-500 hover:bg-red-600"
                                        } text-white transition-colors`}
                                    >
                                        <FiShield />
                                        <span className="font-medium">System Admin Panel</span>
                                    </button>
                                )}

                                {/* Mobile Logout Button */}
                                <button
                                    onClick={() => {
                                        handleLogout()
                                        setIsMobileMenuOpen(false)
                                    }}
                                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg ${
                                        darkMode 
                                            ? "bg-gray-700 hover:bg-gray-600" 
                                            : "bg-gray-200 hover:bg-gray-300"
                                    } transition-colors`}
                                >
                                    <FiLogOut />
                                    <span className="font-medium">Logout</span>
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.header>
        </>
    )
}

export default HeaderComponent
